import { useId, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { IListItem } from "types/gaiaComponents/list";
import Checkbox from "components/common/checkbox.component";
import { Switch } from "components/common/Switch.component";
import "./list.css";

export const ListItem = (props: IListItem) => {
    const expandedContentId = useId();
    const dataId = useId();
    const [internalExpanded, setInternalExpanded] = useState(props.defaultExpanded ?? false);

    const isExpandable = props.expandedContent !== undefined;
    const isExpanded = isExpandable && (props.isExpanded ?? internalExpanded);
    const hasData = !!props.data && props.data.length > 0;

    const toggleExpanded = () => {
        if (props.isDisabled) return;

        setInternalExpanded(!isExpanded);
        props.onExpandedChange?.(!isExpanded);
    };

    const classNames = [
        "ga-list-item",
        props.isSelected && "ga-list-item--selected",
        isExpanded && "ga-list-item--expanded",
        props.isActive && "ga-list-item--active",
        props.isDisabled && "ga-list-item--disabled",
        props.className,
    ].filter(Boolean).join(" ");

    return (
        <li className={classNames}
            data-cy={props.dataCy}
            aria-disabled={props.isDisabled || undefined}>
            <div className="ga-list-item__header">
                <div className="ga-list-item__start">
                    {isExpandable && (
                        <button type="button"
                                className="ga-list-item__toggle"
                                aria-expanded={isExpanded}
                                aria-controls={isExpanded ? expandedContentId : undefined}
                                aria-label={props.toggleAriaLabel}
                                disabled={props.isDisabled}
                                onClick={toggleExpanded}>
                            {isExpanded
                                ? <ChevronDown className="ga-icon"/>
                                : <ChevronRight className="ga-icon"/>}
                        </button>
                    )}
                    {props.isChild && (
                        <span className="ga-list-item__indent ga-list-item__indent--child"/>
                    )}
                    {!isExpandable && !props.isChild && <span className="ga-list-item__indent"/>}
                    {props.selection && (
                        <div className="ga-list-item__selection">
                            <Checkbox isChecked={props.selection.isSelected}
                                      isDisabled={props.isDisabled}
                                      ariaLabel={props.selection.ariaLabel}
                                      onChange={props.selection.onChange}/>
                        </div>
                    )}
                    {props.icon && <div className="ga-list-item__icon">{props.icon}</div>}
                    <div className="ga-list-item__content">
                        {/* The title button stretches over the header (see list.css), so the whole row stays
                            clickable while the controls above stay outside it instead of nested in it */}
                        {props.onClick ? (
                            <button type="button"
                                    className="ga-list-item__title ga-list-item__action"
                                    aria-describedby={hasData ? dataId : undefined}
                                    disabled={props.isDisabled}
                                    onClick={props.onClick}>
                                {props.title}
                            </button>
                        ) : (
                            <span className="ga-list-item__title">{props.title}</span>
                        )}
                        {hasData && (
                            <div className="ga-list-item__data" id={dataId}>
                                {props.data!.map((item, index) => (
                                    <span className="ga-list-item__data-item" key={index}>{item}</span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
                <div className="ga-list-item__end">
                    {props.alert && <div className="ga-list-item__alert">{props.alert}</div>}
                    {props.tags && <div className="ga-list-item__tags">{props.tags}</div>}
                    {props.description && <span className="ga-list-item__description">{props.description}</span>}
                    {props.toggleSwitch && (
                        <div className="ga-list-item__switch">
                            <Switch {...props.toggleSwitch}
                                    isDisabled={props.isDisabled || props.toggleSwitch.isDisabled}/>
                        </div>
                    )}
                    {props.actions && <div className="ga-list-item__actions">{props.actions}</div>}
                </div>
            </div>
            {isExpanded && (
                <div className="ga-list-item__expanded-content" id={expandedContentId}>
                    {props.expandedContent}
                </div>
            )}
        </li>
    );
}
